'use client';
import { useState } from 'react';

export function NewsletterForm({ domain }: { domain: string }) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email');

    try {
      const res = await fetch('/form-submit/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, domain, url: window.location.href }),
      });
      
      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-lg text-center animate-in fade-in">
        <span className="text-emerald-800 text-xs font-bold block">✓ ¡Suscripción confirmada!</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubscribe} className="flex flex-col gap-2">
      <div className="flex gap-2">
        <input 
          type="email" 
          name="email" 
          placeholder="correo@corporativo.com" 
          required 
          className="flex-1 p-2.5 text-xs rounded border border-slate-300 text-slate-900 outline-none focus:border-slate-900" 
        />
        <button 
          type="submit" 
          disabled={status === 'loading'}
          className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded text-xs font-bold transition disabled:opacity-50"
        >
          {status === 'loading' ? 'Enviando...' : 'Suscribir'}
        </button>
      </div>
      {status === 'error' && <p className="text-[11px] text-red-600 font-semibold">Error al conectar.</p>}
    </form>
  );
}

export function ContactForm({ domain }: { domain: string }) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleContact = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    
    const formData = new FormData(e.currentTarget);
    const nombre = formData.get('nombre');
    const email = formData.get('email');
    const mensaje = formData.get('mensaje');

    try {
      const res = await fetch('/form-submit/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre, email, mensaje, domain, url: window.location.href }),
      });

      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="p-8 bg-emerald-50 border-2 border-emerald-400 rounded-xl text-center shadow-sm animate-in fade-in zoom-in-95 duration-300">
        <div className="w-14 h-14 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-black shadow-md">
          ✓
        </div>
        <h4 className="text-emerald-950 font-black text-xl mb-2">¡Consulta Recibida!</h4>
        <p className="text-emerald-800 text-sm leading-relaxed max-w-sm mx-auto mb-6">
          Hemos registrado tu solicitud para <strong>{domain}</strong>. Nuestro equipo técnico revisará tus datos y se pondrá en contacto al correo indicado.
        </p>
        <button 
          type="button" 
          onClick={() => setStatus('idle')} 
          className="text-xs font-bold text-emerald-700 underline hover:text-emerald-900 transition"
        >
          ← Enviar nueva consulta
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleContact} className="flex flex-col gap-4 animate-in fade-in">
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">Nombre Completo</label>
        <input 
          type="text" 
          name="nombre" 
          placeholder="Ingresa tu nombre" 
          required 
          className="w-full p-3 border border-slate-300 rounded text-sm text-slate-900 outline-none focus:border-slate-900" 
        />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">Correo Electrónico</label>
        <input 
          type="email" 
          name="email" 
          placeholder="correo@empresa.com" 
          required 
          className="w-full p-3 border border-slate-300 rounded text-sm text-slate-900 outline-none focus:border-slate-900" 
        />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">Mensaje o Consulta</label>
        <textarea 
          name="mensaje" 
          placeholder="Describe brevemente tus requerimientos o dudas operativas..." 
          rows={4} 
          required 
          className="w-full p-3 border border-slate-300 rounded text-sm text-slate-900 outline-none focus:border-slate-900"
        ></textarea>
      </div>
      {status === 'error' && <p className="text-xs text-red-600 font-semibold">Hubo un problema de conexión. Por favor reintenta.</p>}
      <button 
        type="submit" 
        disabled={status === 'loading'}
        className="w-full bg-slate-950 hover:bg-slate-800 text-white font-bold py-3.5 rounded text-sm transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {status === 'loading' ? 'Procesando Envío...' : 'Enviar Consulta ↗'}
      </button>
    </form>
  );
}
